import React, { useState } from 'react';
import { Typography, Button, Modal, TextArea, TextField } from '@scale/scaleui';
import { AuditPermissionRole } from '@scale/shared/taskerScreening/types';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { parseAnythingSeparatedIds } from '@scale/shared/utils';

interface TaskerScreeningAuditorPermissionReviewModalProps {
  role: AuditPermissionRole;
  open: boolean;
  onClose: () => void;
  onSave: (
    id: string,
    addWorkers: string[],
    removeWorkers: string[],
    disableWorkers: string[],
  ) => void;
}

const PaddedHeader = ({ title }: { title: string }) => {
  return (
    <div className="mt-4 mb-2">
      <Typography.H5>{title}</Typography.H5>
    </div>
  );
};

const TaskerScreeningAuditorPermissionReviewContents: React.FC<
  TaskerScreeningAuditorPermissionReviewModalProps
> = ({ role, onSave }) => {
  const [addWorkersString, setAddWorkersString] = useState<string>();
  const [removeWorkersString, setRemoveWorkersString] = useState<string>();
  const [disableWorkersString, setDisableWorkersString] = useState<string>();
  return (
    <div className="scale-ui scale-theme scale-tailwind w-[600px]">
      <PaddedHeader title="Role Name" />
      <Typography.Paragraph2>{role.name}</Typography.Paragraph2>
      {role.description && (
        <div>
          <PaddedHeader title="Role Description" />
          <Typography.Paragraph2>{role.description}</Typography.Paragraph2>
        </div>
      )}
      <PaddedHeader title="Role Courses" />
      {role.courseIds.map(courseId => (
        <div key={courseId} className="w-full">
          <SmartLink key={courseId} type={INamedObjectTypes.Course} id={courseId} />
        </div>
      ))}
      <PaddedHeader title="Role Course Section Titles" />
      <Typography.Paragraph2>{role.sectionTitles.join(', ')}</Typography.Paragraph2>
      <PaddedHeader title="Currently Permissioned Workers" />
      <TextArea
        id="workersPreview"
        value={(role.workersPermissioned?.length ? role.workersPermissioned : ['none']).join(',\n')}
        disabled
      />
      <PaddedHeader title="Add Permissioned Workers" />
      <TextField
        id="add"
        value={addWorkersString}
        onChange={e => setAddWorkersString(e.target.value)}
        placeholder="Worker IDs (anything separated)"
      />
      <PaddedHeader title="Remove Permissioned Workers" />
      <TextField
        id="remove"
        value={removeWorkersString}
        onChange={e => setRemoveWorkersString(e.target.value)}
        placeholder="Worker IDs (anything separated)"
      />
      <PaddedHeader title="Disable Permissioned Workers" />
      <TextField
        id="disable"
        value={disableWorkersString}
        onChange={e => setDisableWorkersString(e.target.value)}
        placeholder="Worker IDs (anything separated)"
      />
      <Button
        className="mt-4"
        onClick={() =>
          onSave(
            role.id,
            parseAnythingSeparatedIds(addWorkersString ?? ''),
            parseAnythingSeparatedIds(removeWorkersString ?? ''),
            parseAnythingSeparatedIds(disableWorkersString ?? ''),
          )
        }
        variant="primary"
      >
        Save Changes
      </Button>
    </div>
  );
};

const TaskerScreeningAuditorPermissionReviewModal: React.FC<
  TaskerScreeningAuditorPermissionReviewModalProps
> = ({ open, onClose, role, onSave }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      theme="scale-theme"
      title="Audit Permission Role Creation"
      innerClassName="overflow-y-auto"
    >
      <TaskerScreeningAuditorPermissionReviewContents
        open={open}
        onClose={onClose}
        role={role}
        onSave={onSave}
      />
    </Modal>
  );
};

export default TaskerScreeningAuditorPermissionReviewModal;
